import { useEffect, useId, useRef } from "react";
import { useReducedMotion } from "framer-motion";
import { deform, ribbon, rubber, type Pt } from "./ribbon";
import { frameDt, settled, step, type Spring } from "./motion";
import { lineBoxes } from "./weave";
import { bare as bareOf, lay, reach, route, stretch, within, type Box, type Laid, type Room } from "./threadRoute";

/*
  The landing's one thread, drawn. Two layers across the whole of <main>: one
  under the page's content (over the coloured fields, under the type, the
  panels and the comparison sheet, so the strip runs behind letters and slips
  under sheets) and two over it, for the stretches where the thread crosses in
  front: flat where it is woven (the Knot) or lies on the Brane's card, lifted where it crosses itself (the Loop). Each layer draws the strip twice, clipped by field: vermilion on the
  bare paper, paper on the coloured fields (the rooms, the comparison's
  columns), so it reads as cut from another sheet where it crosses into one.

  The thread grows down the page as it is read: its tip is the target of a
  spring, about three quarters of the way down the screen, and it pulls back
  when the reader scrolls up. On arrival it runs from its loose cut end round
  the hero headline, never across a word. It can be taken in hand where it lies in
  the open (the loose ends, the String's swing, the Loop), and springs back.

  The page is tall and the strip runs all of it, so each frame draws only the
  stretch near the screen, the fields are geometric clips (a luminance mask
  the size of the page costs a phone a page-sized buffer every frame), a path
  is written only when it changes, and on a touch screen the lift is a hard
  shade under the strip rather than a blur over a page-tall layer.

  With reduced motion it is drawn whole and lies still.
*/

/** How far down the screen the thread's tip leads the reader. */
const LEAD = 0.72;
/** The tip: critically damped, so the strip runs on and stops without bouncing back. */
const TIP_K = 40;
const TIP_C = 12.6;
const SNAP_K = 120;
const SNAP_C = 10;
const PULL_LIMIT = 110;
const PULL_REACH = 90;
/** How far above and below the screen the strip is drawn, in screens: scrolling never outruns it. */
const BAND = 1;

const rect = (b: Box) => `M${b.left} ${b.top}h${b.width}v${b.height}h${-b.width}Z`;

/** Write a path only when it changes: an unchanged write still repaints the whole of it. */
const put = (el: SVGPathElement | null, d: string) => {
  if (el && el.getAttribute("d") !== d) el.setAttribute("d", d);
};

const rgbaAlpha = (c: string) => {
  const m = c.match(/rgba?\(([^)]+)\)/);
  if (!m) return 1;
  const parts = m[1].split(",");
  return parts.length === 4 ? Number(parts[3]) : 1;
};

export function Thread() {
  const under = useRef<SVGSVGElement | null>(null);
  const over = useRef<SVGSVGElement | null>(null);
  const underInk = useRef<SVGPathElement | null>(null);
  const underPaper = useRef<SVGPathElement | null>(null);
  const overInk = useRef<SVGPathElement | null>(null);
  const overPaper = useRef<SVGPathElement | null>(null);
  const overShade = useRef<SVGPathElement | null>(null);
  const front = useRef<SVGSVGElement | null>(null);
  const flatInk = useRef<SVGPathElement | null>(null);
  const flatPaper = useRef<SVGPathElement | null>(null);
  const grip = useRef<SVGPathElement | null>(null);
  const bare = useRef<SVGPathElement | null>(null);
  const field = useRef<SVGPathElement | null>(null);
  const reduced = useReducedMotion() ?? false;
  const id = useId().replace(/:/g, "");

  useEffect(() => {
    const host = under.current?.parentElement;
    if (!host) return;
    let laid: Laid | null = null;
    let tip: Spring = { x: 0, v: 0 };
    let target = 0;
    let held: [Spring, Spring] = [
      { x: 0, v: 0 },
      { x: 0, v: 0 },
    ];
    let hand: { id: number; from: Pt; at: Pt; t: number } | null = null;
    let at: Pt = [0, 0];
    // The page's stretch on screen, in the host's pixels.
    let view: [number, number] = [0, 0];
    let raf = 0;
    let last = 0;

    const layout = () => {
      const m = host.getBoundingClientRect();
      const box = (el: Element | null): Box | null => {
        if (!el) return null;
        const r = el.getBoundingClientRect();
        return { left: r.left - m.left, top: r.top - m.top, width: r.width, height: r.height };
      };
      const room = (name: string): Room | null => {
        const section = host.querySelector(`#${name}`);
        const b = box(section);
        const figure = box(section?.querySelector(".sh-figure") ?? null);
        const text = box(section?.querySelector(".sh-room__text") ?? null);
        return b && figure && text ? { section: b, figure, text } : null;
      };
      const heroTitle = host.querySelector<HTMLElement>("#sh-hero-title");
      const closeTitle = host.querySelector<HTMLElement>("#sh-close-title");
      const hero = heroTitle ? lineBoxes(heroTitle, m) : [];
      const close = closeTitle ? lineBoxes(closeTitle, m) : [];
      const string = room("strings");
      const brane = room("branes") ?? undefined;
      const knot = room("knots");
      const loop = room("loops");
      if (!hero.length || !close.length || !string || !knot || !loop) {
        laid = null;
        return;
      }
      laid = lay(
        route({
          width: m.width,
          height: m.height,
          margin: string.text.left,
          hero,
          close,
          compare: Array.from(host.querySelectorAll(".sh-compare__col")).map((el) => box(el)!),
          string,
          brane,
          knot,
          loop,
        })
      );

      // The fields: bare paper keeps the vermilion strip; on a coloured field (a room, a comparison
      // column, the phone's sheet) it turns to paper. The vermilion is clipped to the bare paper
      // rather than drawn under the paper, so no vermilion fringe shows at the paper strip's edges.
      const coloured = [
        ...Array.from(host.children).filter(
          (el) => !(el instanceof SVGElement) && rgbaAlpha(getComputedStyle(el).backgroundColor) > 0
        ),
        ...Array.from(host.querySelectorAll(".sh-compare__col, .sh-stage__sheet")),
      ].map((el) => box(el)!);
      const fields = coloured;
      bare.current?.setAttribute("d", bareOf(m.width, m.height, fields).map(rect).join(""));
      field.current?.setAttribute("d", fields.map(rect).join(""));
      for (const svg of [under.current, over.current, front.current]) {
        svg?.setAttribute("width", String(m.width));
        svg?.setAttribute("height", String(m.height));
        svg?.setAttribute("viewBox", `0 0 ${m.width} ${m.height}`);
      }
    };

    const aim = () => {
      if (!laid) return;
      const top = -host.getBoundingClientRect().top;
      const vh = window.innerHeight;
      view = [top - vh * BAND, top + vh * (1 + BAND)];
      target = reduced ? laid.total : reach(laid, top + vh * LEAD);
    };

    const paint = () => {
      if (!laid) return;
      const drawn = Math.max(0, Math.min(laid.total, tip.x));
      const by: Pt = [held[0].x, held[1].x];
      const pts = by[0] || by[1] ? deform(laid.pts, at, by, PULL_REACH) : laid.pts;
      // Only the stretch near the screen: the rest is drawn as the reader scrolls to it.
      const [from, to] = within(laid, drawn, view[0], view[1], pts) ?? [0, 0];
      const body = stretch(laid, from, to, pts);
      const d =
        body.pts.length > 1
          ? ribbon(body.pts, body.widths, { start: from === 0 ? 0.7 : 0, end: to === drawn && drawn < laid.total ? 0.45 : 0 })
          : "";
      put(underInk.current, d);
      put(underPaper.current, d);
      const crossings = (lift: boolean) =>
        laid!.over
          .filter((o) => o.lift === lift)
          .map((o) => stretch(laid!, Math.max(o.from, from), Math.min(o.to, to), pts))
          .filter((s) => s.pts.length > 1)
          .map((s) => ribbon(s.pts, s.widths))
          .join("");
      const lifted = crossings(true);
      const flat = crossings(false);
      put(overShade.current, lifted);
      put(overInk.current, lifted);
      put(overPaper.current, lifted);
      put(flatInk.current, flat);
      put(flatPaper.current, flat);
      if (!reduced) {
        put(
          grip.current,
          laid.grip
            .map(([a, b]) => stretch(laid!, Math.max(a, from), Math.min(b, to), pts).pts)
            .filter((p) => p.length > 1)
            .map((p) => "M" + p.map(([x, y]) => `${Math.round(x)} ${Math.round(y)}`).join("L"))
            .join("")
        );
      }
    };

    const tick = (now: number) => {
      const dt = frameDt(now, last);
      last = now;
      tip = step(tip, target, dt, TIP_K, TIP_C);
      if (!hand) held = [step(held[0], 0, dt, SNAP_K, SNAP_C), step(held[1], 0, dt, SNAP_K, SNAP_C)];
      paint();
      const still = !hand && Math.abs(tip.x - target) < 0.5 && Math.abs(tip.v) < 0.5 && settled(held[0], 0) && settled(held[1], 0);
      if (still) {
        tip = { x: target, v: 0 };
        paint();
        raf = 0;
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    const wake = () => {
      if (raf) return;
      if (reduced) {
        tip = { x: target, v: 0 };
        paint();
        return;
      }
      last = performance.now();
      raf = requestAnimationFrame(tick);
    };

    const refresh = () => {
      layout();
      aim();
      wake();
      paint();
    };
    refresh();
    const ro = new ResizeObserver(refresh);
    ro.observe(host);
    document.fonts?.ready.then(refresh);
    const onScroll = () => {
      aim();
      wake();
      // Settled, the loop is asleep; the stretch on screen still follows the scroll.
      if (!raf) paint();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    // In hand: the thread goes where the hand puts it, and keeps the hand's speed when let go.
    const local = (e: PointerEvent): Pt => {
      const m = host.getBoundingClientRect();
      return [e.clientX - m.left, e.clientY - m.top];
    };
    const onDown = (e: PointerEvent) => {
      if (hand) return;
      const p = local(e);
      at = p;
      hand = { id: e.pointerId, from: [p[0] - held[0].x, p[1] - held[1].x], at: p, t: performance.now() };
      try {
        grip.current?.setPointerCapture(e.pointerId);
      } catch {
        // A pointer the browser no longer tracks: the pull still works while it stays over the thread.
      }
      host.classList.add("is-holding");
    };
    const onMove = (e: PointerEvent) => {
      if (!hand || e.pointerId !== hand.id) return;
      const p = local(e);
      const now = performance.now();
      const dt = Math.max(1, now - hand.t) / 1000;
      const next: Pt = [rubber(p[0] - hand.from[0], PULL_LIMIT), rubber(p[1] - hand.from[1], PULL_LIMIT)];
      held = [
        { x: next[0], v: (next[0] - held[0].x) / dt },
        { x: next[1], v: (next[1] - held[1].x) / dt },
      ];
      hand.t = now;
      wake();
    };
    const onUp = (e: PointerEvent) => {
      if (!hand || e.pointerId !== hand.id) return;
      hand = null;
      host.classList.remove("is-holding");
      wake();
    };
    const g = grip.current;
    if (!reduced && g) {
      g.addEventListener("pointerdown", onDown);
      g.addEventListener("pointermove", onMove);
      g.addEventListener("pointerup", onUp);
      g.addEventListener("pointercancel", onUp);
    }
    return () => {
      ro.disconnect();
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      g?.removeEventListener("pointerdown", onDown);
      g?.removeEventListener("pointermove", onMove);
      g?.removeEventListener("pointerup", onUp);
      g?.removeEventListener("pointercancel", onUp);
      host.classList.remove("is-holding");
    };
  }, [reduced]);

  const bareId = `${id}-bare`;
  const fieldId = `${id}-field`;
  return (
    <>
      <svg ref={under} className="sh-thread sh-thread--under" aria-hidden="true">
        <defs>
          <clipPath id={bareId} clipPathUnits="userSpaceOnUse">
            <path ref={bare} />
          </clipPath>
          <clipPath id={fieldId} clipPathUnits="userSpaceOnUse">
            <path ref={field} />
          </clipPath>
        </defs>
        <path ref={underInk} className="sh-strip sh-strip--vermilion" clipPath={`url(#${bareId})`} />
        <path ref={underPaper} className="sh-strip sh-strip--paper" clipPath={`url(#${fieldId})`} />
      </svg>
      <svg ref={front} className="sh-thread sh-thread--front" aria-hidden="true">
        <path ref={flatInk} className="sh-strip sh-strip--vermilion" clipPath={`url(#${bareId})`} />
        <path ref={flatPaper} className="sh-strip sh-strip--paper" clipPath={`url(#${fieldId})`} />
      </svg>
      <svg ref={over} className="sh-thread sh-thread--over" aria-hidden="true">
        <path ref={overShade} className="sh-thread__shade" />
        <path ref={overInk} className="sh-strip sh-strip--vermilion" clipPath={`url(#${bareId})`} />
        <path ref={overPaper} className="sh-strip sh-strip--paper" clipPath={`url(#${fieldId})`} />
        {!reduced && <path ref={grip} className="sh-thread__grip" strokeWidth={56} />}
      </svg>
    </>
  );
}
